/**
 * Home Page
 * The main landing page after login.
 * Centered greeting, AI prompt input, and quick actions.
 */
import { useState } from "react";
import { Link } from "@/components/ui/link";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "@/hooks/use-translations";
import {
	FolderKanban,
	FileText,
	LayoutGrid,
	ArrowUp,
	Globe,
	Paperclip,
	Camera,
	MessageSquare,
	Users,
} from "lucide-react";
import { motion } from "motion/react";
import { useAuth } from "@/hooks/use-auth";
import { useActiveTeam } from "@/hooks/use-team";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CreateProjectModal } from "@/components/create-project-modal";
import { Button } from "@/components/ui/button";

function getGreeting(): string {
	const hour = new Date().getHours();
	if (hour < 6) return "goodNight";
	if (hour < 12) return "goodMorning";
	if (hour < 18) return "goodAfternoon";
	return "goodEvening";
}

export function HomePage() {
	const t = useTranslations("home");
	const { user } = useAuth();
	const { activeTeam } = useActiveTeam();
	const navigate = useNavigate();
	const [prompt, setPrompt] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [showCreateModal, setShowCreateModal] = useState(false);

	const firstName = user?.name?.split(" ")[0] || "";

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!prompt.trim() || isSubmitting) return;

		setIsSubmitting(true);
		try {
			// No project_id — the backend creates a new project from this
			// message and hands back its id (see lib/api.ts's sendChatMessage).
			const res = await api.sendChatMessage(
				undefined,
				prompt.trim(),
				undefined,
				activeTeam?.id,
			);
			setPrompt("");
			navigate(`/workspace/${res.chatId}`);
		} catch (error) {
			const message =
				error instanceof Error ? error.message : "Failed to send message";
			toast.error(message);
		} finally {
			setIsSubmitting(false);
		}
	};

	const quickActions = [
		{
			key: "createProject",
			labelKey: "actions.createProject",
			icon: FolderKanban,
			onClick: () => setShowCreateModal(true),
		},
		{
			key: "fillBrief",
			href: "/projects/new/brief",
			labelKey: "actions.fillBrief",
			icon: FileText,
		},
		{
			key: "useTemplate",
			href: "/projects",
			labelKey: "actions.useTemplate",
			icon: LayoutGrid,
		},
	];

	return (
		<div
			className="flex flex-col rounded-3xl border border-border-subtle bg-bg-surface shadow-sm overflow-hidden"
			style={{
				height: "calc(100vh - 64px - 74px)",
				minHeight: "480px",
			}}
		>
			{/* Centered Content */}
			<div className="flex-1 flex flex-col items-center justify-center px-4 -mt-10">
				{/* Greeting */}
				<motion.div
					initial={{ opacity: 0, y: -20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.5, ease: "easeOut" }}
					className="text-center mb-8"
				>
					<h1 className="text-4xl sm:text-5xl font-semibold text-text-primary tracking-tight">
						{t(getGreeting())} {firstName}
					</h1>
					<p className="mt-3 text-2xl sm:text-3xl text-text-tertiary font-light">
						{t("subtitle")}
					</p>
				</motion.div>

				{/* Prompt Area */}
				<motion.div
					initial={{ opacity: 0, y: 15 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.4, delay: 0.15 }}
					className="w-full max-w-2xl"
				>
					{/* Brand Voice & Audience Pills */}
					<div className="flex flex-wrap items-center gap-2 mb-3">
						<button className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-border-subtle bg-bg-canvas text-xs font-medium text-text-secondary hover:bg-bg-sunken hover:text-text-primary transition-colors">
							<MessageSquare size={13} />
							{t("setBrandVoice")}
						</button>
						<button className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-border-subtle bg-bg-canvas text-xs font-medium text-text-secondary hover:bg-bg-sunken hover:text-text-primary transition-colors">
							<Users size={13} />
							{t("setAudience")}
						</button>
					</div>

					{/* Input Box */}
					<form onSubmit={handleSubmit}>
						<div className="relative rounded-2xl bg-bg-surface border border-border-control focus-within:ring-2 focus-within:ring-focus-ring focus-within:ring-offset-2 focus-within:ring-offset-bg-surface shadow-md">
							<textarea
								value={prompt}
								onChange={(e) => setPrompt(e.target.value)}
								placeholder={t("placeholder")}
								rows={3}
								className="w-full bg-transparent text-text-primary placeholder:text-text-tertiary text-sm px-4 pt-4 pb-12 resize-none focus:outline-none"
								onKeyDown={(e) => {
									if (e.key === "Enter" && !e.shiftKey) {
										e.preventDefault();
										handleSubmit(e);
									}
								}}
							/>
							{/* Bottom toolbar */}
							<div className="absolute bottom-3 right-3 flex items-center gap-1.5">
								<button
									type="button"
									className="p-1.5 rounded-lg text-text-tertiary hover:text-text-secondary hover:bg-bg-sunken transition-colors"
								>
									<Camera size={16} />
								</button>
								<button
									type="button"
									className="p-1.5 rounded-lg text-text-tertiary hover:text-text-secondary hover:bg-bg-sunken transition-colors"
								>
									<Globe size={16} />
								</button>
								<button
									type="button"
									className="p-1.5 rounded-lg text-text-tertiary hover:text-text-secondary hover:bg-bg-sunken transition-colors"
								>
									<Paperclip size={16} />
								</button>
								<Button
									type="submit"
									size="icon-sm"
									disabled={!prompt.trim() || isSubmitting}
									className="ml-1"
								>
									<ArrowUp size={16} strokeWidth={2.5} />
								</Button>
							</div>
						</div>
					</form>

					{/* Quick Action Buttons */}
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.3, delay: 0.35 }}
						className="flex flex-wrap items-center justify-center gap-3 mt-4"
					>
						{quickActions.map((action) => {
							const Icon = action.icon;
							return action.onClick ? (
								<Button
									key={action.key}
									type="button"
									variant="secondary"
									size="sm"
									onClick={action.onClick}
									className="px-4"
								>
									<Icon size={14} color="currentColor" />
									{t(action.labelKey)}
								</Button>
							) : (
								<Button
									key={action.key}
									asChild
									variant="secondary"
									size="sm"
									className="px-4"
								>
									<Link href={action.href!}>
										<Icon size={14} color="currentColor" />
										{t(action.labelKey)}
									</Link>
								</Button>
							);
						})}
					</motion.div>
				</motion.div>
			</div>

			<CreateProjectModal
				open={showCreateModal}
				onClose={() => setShowCreateModal(false)}
				onCreated={(chatId) => navigate(`/workspace/${chatId}`)}
			/>
		</div>
	);
}
